import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  MATIERES,
  NIVEAUX,
  RESOURCE_TYPES,
  STATUSES,
  NiveauBadge,
  MatiereBadge,
  ResourceTypeBadge,
  StatusBadge,
  formatRelativeTime,
} from "./constants";
import {
  listResources,
  deleteResource,
  duplicateResource,
  shareResource,
  unshareResource,
} from "../../lib/api";
import {
  Search,
  Filter,
  Grid3X3,
  List,
  MoreHorizontal,
  ExternalLink,
  Edit,
  Copy,
  Share2,
  Trash2,
  FileText,
  X,
  SlidersHorizontal,
  Users,
} from "lucide-react";
import { VariantsViewDialog } from "./VariantsViewDialog";

function ResourceGridCard({ resource, onAction }) {
  const navigate = useNavigate();

  const handleOpen = () => {
    // Visual resources (fiche) go to visual editor, text resources go to text editor
    if (resource.resource_type === 'fiche' || resource.elements) {
      navigate(`/create/${resource.id}`);
    } else {
      navigate(`/editor/${resource.id}`);
    }
  };

  return (
    <Card className="group hover:shadow-md transition-all duration-200 overflow-hidden">
      <CardContent className="p-0">
        <div
          className="h-32 bg-gradient-to-br from-slate-50 to-slate-100 relative cursor-pointer"
          onClick={handleOpen}
        >
          {resource.thumbnail ? (
            <img
              src={resource.thumbnail}
              alt={resource.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <FileText className="w-10 h-10 text-slate-300" />
            </div>
          )}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors" />
          <div className="absolute top-2 right-2 flex items-center gap-1">
            {resource.has_variants && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-indigo-100 text-indigo-700">
                <Users className="w-3 h-3 mr-0.5" />
                {resource.variants_count}
              </Badge>
            )}
            <StatusBadge status={resource.status} />
          </div>
        </div>

        <div className="p-3">
          <h3
            className="font-semibold text-slate-800 text-sm line-clamp-1 cursor-pointer hover:text-indigo-600 mb-2"
            onClick={handleOpen}
          >
            {resource.title}
          </h3>

          <div className="flex flex-wrap gap-1 mb-3">
            {resource.niveau && <NiveauBadge niveau={resource.niveau} className="text-xs" />}
            {resource.matiere && <MatiereBadge matiere={resource.matiere} className="text-xs" />}
            {resource.resource_type && <ResourceTypeBadge type={resource.resource_type} className="text-xs" />}
          </div>

          <div className="flex items-center justify-between text-xs text-slate-400">
            <span>{formatRelativeTime(resource.updated_at)}</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleOpen}>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Ouvrir
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onAction("edit", resource)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Modifier
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onAction("duplicate", resource)}>
                  <Copy className="w-4 h-4 mr-2" />
                  Dupliquer
                </DropdownMenuItem>
                {resource.has_variants && (
                  <DropdownMenuItem onClick={() => onAction("variants", resource)}>
                    <Users className="w-4 h-4 mr-2" />
                    Voir les variantes ({resource.variants_count})
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                {resource.is_shared ? (
                  <DropdownMenuItem onClick={() => onAction("unshare", resource)}>
                    <Share2 className="w-4 h-4 mr-2" />
                    Retirer du partage
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => onAction("share", resource)}>
                    <Share2 className="w-4 h-4 mr-2" />
                    Partager
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onAction("delete", resource)}
                  className="text-red-600"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ResourceListItem({ resource, onAction }) {
  const navigate = useNavigate();

  const handleOpen = () => {
    // Visual resources (fiche) go to visual editor, text resources go to text editor
    if (resource.resource_type === 'fiche' || resource.elements) {
      navigate(`/create/${resource.id}`);
    } else {
      navigate(`/editor/${resource.id}`);
    }
  };

  return (
    <div className="flex items-center gap-4 p-3 bg-white rounded-lg border hover:border-indigo-200 transition-colors">
      <div
        className="w-16 h-16 bg-slate-100 rounded-lg flex-shrink-0 cursor-pointer overflow-hidden"
        onClick={handleOpen}
      >
        {resource.thumbnail ? (
          <img
            src={resource.thumbnail}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FileText className="w-6 h-6 text-slate-300" />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <h3
          className="font-semibold text-slate-800 text-sm cursor-pointer hover:text-indigo-600 line-clamp-1"
          onClick={handleOpen}
        >
          {resource.title}
        </h3>
        <div className="flex flex-wrap gap-1 mt-1">
          {resource.niveau && <NiveauBadge niveau={resource.niveau} className="text-xs" />}
          {resource.matiere && <MatiereBadge matiere={resource.matiere} className="text-xs" />}
          <StatusBadge status={resource.status} className="text-xs" />
        </div>
        <p className="text-xs text-slate-400 mt-1">{formatRelativeTime(resource.updated_at)}</p>
      </div>

      <div className="flex items-center gap-1">
        <Button variant="ghost" size="sm" onClick={handleOpen}>
          <ExternalLink className="w-4 h-4" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onAction("duplicate", resource)}>
              <Copy className="w-4 h-4 mr-2" />
              Dupliquer
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAction("share", resource)}>
              <Share2 className="w-4 h-4 mr-2" />
              Partager
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onAction("delete", resource)}
              className="text-red-600"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

export function TeacherResourcesTab() {
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    niveau: "",
    matiere: "",
    resource_type: "",
    status: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [variantsDialogOpen, setVariantsDialogOpen] = useState(false);
  const [selectedResourceForVariants, setSelectedResourceForVariants] = useState(null);

  const fetchResources = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (search) params.search = search;
      if (filters.niveau) params.niveau = filters.niveau;
      if (filters.matiere) params.matiere = filters.matiere;
      if (filters.resource_type) params.resource_type = filters.resource_type;
      if (filters.status) params.status = filters.status;

      const { data } = await listResources(params);
      setResources(data);
    } catch (err) {
      console.error("Error fetching resources:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(fetchResources, 300);
    return () => clearTimeout(debounce);
  }, [search, filters]);

  const handleAction = async (action, resource) => {
    switch (action) {
      case "edit":
        // Visual resources (fiche) go to visual editor, text resources go to text editor
        if (resource.resource_type === 'fiche' || resource.elements) {
          navigate(`/create/${resource.id}`);
        } else {
          navigate(`/editor/${resource.id}`);
        }
        break;
      case "duplicate":
        try {
          await duplicateResource(resource.id);
          fetchResources();
        } catch (err) {
          console.error("Error duplicating:", err);
        }
        break;
      case "share":
        try {
          await shareResource(resource.id);
          fetchResources();
        } catch (err) {
          console.error("Error sharing:", err);
        }
        break;
      case "unshare":
        try {
          await unshareResource(resource.id);
          fetchResources();
        } catch (err) {
          console.error("Error unsharing:", err);
        }
        break;
      case "delete":
        if (window.confirm("Supprimer cette ressource ?")) {
          try {
            await deleteResource(resource.id);
            fetchResources();
          } catch (err) {
            console.error("Error deleting:", err);
          }
        }
        break;
      case "variants":
        setSelectedResourceForVariants(resource);
        setVariantsDialogOpen(true);
        break;
    }
  };

  const clearFilters = () => {
    setFilters({ niveau: "", matiere: "", resource_type: "", status: "" });
    setSearch("");
  };

  const hasActiveFilters = Object.values(filters).some(Boolean) || search;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800 mb-1">Mes ressources</h2>
          <p className="text-slate-500 text-sm">
            {resources.length} ressource{resources.length !== 1 ? "s" : ""}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === "grid" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setViewMode("grid")}
          >
            <Grid3X3 className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === "list" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setViewMode("list")}
          >
            <List className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button
            variant={showFilters ? "secondary" : "outline"}
            onClick={() => setShowFilters(!showFilters)}
          >
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            Filtres
            {hasActiveFilters && (
              <Badge className="ml-2 bg-indigo-600">!</Badge>
            )}
          </Button>
        </div>

        {showFilters && (
          <div className="p-4 bg-slate-50 rounded-lg space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Select
                value={filters.niveau || "all"}
                onValueChange={(v) => setFilters({ ...filters, niveau: v === "all" ? "" : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Niveau" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les niveaux</SelectItem>
                  {NIVEAUX.map((n) => (
                    <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.matiere || "all"}
                onValueChange={(v) => setFilters({ ...filters, matiere: v === "all" ? "" : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Matiere" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les matieres</SelectItem>
                  {MATIERES.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.resource_type || "all"}
                onValueChange={(v) => setFilters({ ...filters, resource_type: v === "all" ? "" : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  {RESOURCE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.status || "all"}
                onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  {STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Effacer les filtres
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Resources List */}
      {loading ? (
        <div className={viewMode === "grid" 
          ? "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" 
          : "space-y-2"
        }>
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className={viewMode === "grid" ? "h-48" : "h-20"} />
          ))}
        </div>
      ) : resources.length === 0 ? (
        <Card className="bg-slate-50 border-dashed">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 mb-4">
              {hasActiveFilters
                ? "Aucune ressource ne correspond aux filtres"
                : "Aucune ressource pour le moment"
              }
            </p>
            {hasActiveFilters ? (
              <Button variant="outline" onClick={clearFilters}>
                Effacer les filtres
              </Button>
            ) : (
              <Button onClick={() => navigate("/create")} className="bg-indigo-600 hover:bg-indigo-700">
                Créer ma première ressource
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {resources.map((resource) => (
            <ResourceGridCard
              key={resource.id}
              resource={resource}
              onAction={handleAction}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {resources.map((resource) => (
            <ResourceListItem
              key={resource.id}
              resource={resource}
              onAction={handleAction}
            />
          ))}
        </div>
      )}
      
      {/* Variants Dialog */}
      <VariantsViewDialog
        open={variantsDialogOpen}
        onOpenChange={setVariantsDialogOpen}
        resource={selectedResourceForVariants}
      />
    </div>
  );
}
